// ============================================
// 2026-10-19（体积元素 S4）：云团 impostor 布局（纯函数，可单测）
//  - 面向相机的噪声贴片（billboard/sprite）叠加，不用 raymarch；成本≈普通透明面片
//  - 布局确定性：同一 id 永远同一布局（随场景保存/刷新/预览发布一致）
//  - 分布：球面 Fibonacci 均匀分布（避免聚簇），收敛到盒体 60% 范围内
// ============================================
export type P3 = [number, number, number];

export interface CloudSpriteSpec {
  /** 相对体积中心的局部坐标（世界单位） */
  position: P3;
  /** 贴片缩放因子（0.6~1.4；调用侧乘以体积尺寸） */
  scale: number;
  /** 初始旋转（弧度；随 noiseSpeed 缓慢自转） */
  rotation: number;
}

/** 简单确定性哈希（FNV-1a 变体）→ [0,1) */
function hash01(seed: string, i: number): number {
  let h = 2166136261 ^ i;
  for (let k = 0; k < seed.length; k++) {
    h ^= seed.charCodeAt(k);
    h = Math.imul(h, 16777619);
  }
  h ^= h >>> 13;
  h = Math.imul(h, 0x5bd1e995);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

/** 云量（0~1）→ 贴片数量（3~16） */
export function cloudCountFromDensity(density: number): number {
  const d = Number.isFinite(density) ? Math.max(0, Math.min(1, density)) : 0.5;
  return Math.max(3, Math.min(16, Math.round(3 + d * 13)));
}

/**
 * 云团贴片布局：球面 Fibonacci 均匀分布 + 确定性抖动
 * @param seed 稳定种子（用体积 id）
 * @param size 体积半长 [x,y,z]
 * @param count 贴片数（建议 cloudCountFromDensity）
 */
export function cloudSpriteLayout(seed: string, size: P3, count: number): CloudSpriteSpec[] {
  const n = Math.max(1, Math.floor(count));
  const [sx, sy, sz] = [
    Math.max(0.1, Math.abs(size[0])),
    Math.max(0.1, Math.abs(size[1])),
    Math.max(0.1, Math.abs(size[2])),
  ];
  const out: CloudSpriteSpec[] = [];
  const golden = Math.PI * (3 - Math.sqrt(5)); // 黄金角
  for (let i = 0; i < n; i++) {
    // 球面均匀点
    const y = 1 - (i / Math.max(1, n - 1)) * 2; // 1 → -1
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = golden * i;
    const ux = Math.cos(theta) * r;
    const uy = y;
    const uz = Math.sin(theta) * r;
    // 半径因子（0.25~0.6，确定性抖动）与逐轴收敛
    const rf = 0.25 + 0.35 * hash01(seed, i * 3 + 1);
    const jx = (hash01(seed, i * 3 + 2) - 0.5) * 0.2;
    const jz = (hash01(seed, i * 3 + 3) - 0.5) * 0.2;
    out.push({
      position: [
        ux * sx * rf + jx * sx,
        uy * sy * rf * 0.9,
        uz * sz * rf + jz * sz,
      ],
      scale: 0.6 + 0.8 * hash01(seed, i * 3 + 11),
      rotation: hash01(seed, i * 3 + 21) * Math.PI * 2,
    });
  }
  return out;
}
